import { Box, Grid, Stack, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { useDialog } from "lib/@hooks/useDialog"

import { FIRST_COL_WIDTH, OTHER_COLS_WIDTH, ROLES_TO_DISPLAY } from "./constants"
import { RolesDialog } from "./roles-dialog"

import { useState } from "react"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Iconify from "minimals-template/components/Iconify"
import { StandardCheckBox } from "lib/@components/standard-checkbox"
import { confirm } from "lib/@dialogs/confirm"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { deleteRole, updateRole } from "lib/permissions/roles"
import { lowercaseAndReplaceSpaceWithHyphens } from "lib/permissions/helpers"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { UserRolesTitle } from "slot-definitions"
import { TruncatedTypography } from "lib/@components/truncated-typography"

function RoleControlMenu({ role }) {
    const { clientId } = useBoundContext()
    const [anchorEl, setAnchorEl] = useState(null)
    const update = useDialog(<RolesDialog role={role} />)
    const testId = lowercaseAndReplaceSpaceWithHyphens(role?.name)
    return (
        <>
            <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={handleClick}
                data-cy={`${testId}-menu`}
            >
                <Iconify icon="entypo:dots-three-vertical" sx={{ fontSize: "12px" }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                <MenuItem data-cy={`edit-${testId}-button`} onClick={handleEdit}>
                    Edit
                </MenuItem>
                <MenuItem data-cy={`delete-${testId}-button`} onClick={handleDelete}>
                    Delete
                </MenuItem>
            </Menu>
        </>
    )

    function handleClick(event) {
        setAnchorEl(event.currentTarget)
    }

    function handleClose() {
        setAnchorEl(null)
    }

    async function handleEdit() {
        const newRole = {
            name: role?.name,
            description: role?.description || "",
            demands: role?.demands?.map((demand) => demand.name),
        }

        try {
            const updatedRole = await update(newRole)
            await updateRole({
                roleId: updatedRole.id,
                roleDemands: updatedRole?.demands,
                roleDescription: updatedRole?.description,
                roleName: updatedRole?.name,
            })
        } catch (error) {
            console.error(error)
        }
        handleClose()
    }

    async function handleDelete() {
        if (await confirm(`Are you sure you want to remove role ${role?.name}?`, "Delete Role")) {
            try {
                const response = await deleteRole({ id: role.id, client: clientId })
                if (response === true) {
                    infoSnackbar(`Role ${role?.name} deleted successfully`)
                } else {
                    warningSnackbar(`Role ${role?.name} could not be deleted`)
                }
            } catch (error) {
                console.error(error)
                errorSnackbar(`Error: Role ${role?.name} could not be deleted`)
            }
        }
    }
}

export function RolesTableHeadCell({ role, demands, empty = false }) {
    const { clientId: client } = useBoundContext()
    const selectAllCheckBoxSet = role?.demands.length === demands?.length
    const selectAllName = `${lowercaseAndReplaceSpaceWithHyphens(role?.name)}-select-all`
    return (
        <TableCell
            style={{ boxShadow: "none" }}
            sx={{
                width: OTHER_COLS_WIDTH,
                backgroundColor: "transparent",
            }}
        >
            {!empty ? (
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    direction="column"
                    sx={{ minWidth: OTHER_COLS_WIDTH, padding: 0, margin: 0 }}
                >
                    <Grid container width="100%" spacing={0}>
                        <Grid item xs={12} sm={10} sx={{ textAlign: "center" }}>
                            <TruncatedTypography
                                component={"div"}
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{
                                    backgroundColor: "transparent",
                                }}
                            >
                                {role.name}
                            </TruncatedTypography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <RoleControlMenu role={role} demands={demands} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="block" text-align="center" ml={1} mr={1}>
                                <StandardCheckBox
                                    indeterminate={role.demands.length > 0 && !selectAllCheckBoxSet}
                                    onChange={handleSelectAllToggle}
                                    value={selectAllCheckBoxSet}
                                    sx={{ display: "block", textAlign: "center", marginLeft: 1 }}
                                    data-cy={selectAllName}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Stack>
            ) : (
                <>{/* spacer */} </>
            )}
        </TableCell>
    )

    async function handleSelectAllToggle() {
        if (selectAllCheckBoxSet) {
            await updateRole({ roleId: role.id, roleDemands: [], client })
        } else {
            await updateRole({
                roleId: role.id,
                roleDemands: demands.map((demand) => demand.name),
                client,
            })
        }
    }
}

export function RolesTableHead({ roles, demands }) {
    return (
        <TableHead>
            <TableRow>
                <TableCell
                    style={{ boxShadow: "none" }}
                    align="left"
                    sx={{ width: FIRST_COL_WIDTH, backgroundColor: "transparent" }}
                >
                    <Stack alignItems="flex-start" justifyContent="flex-start" direction="column">
                        <UserRolesTitle.Slot>
                            <Typography component="div" variant="h4" sx={{ ml: -1, mb: 4 }}>
                                User Roles
                            </Typography>
                        </UserRolesTitle.Slot>
                    </Stack>
                </TableCell>
                {roles.map((role) => (
                    <RolesTableHeadCell key={role.id} role={role} demands={demands} />
                ))}
                {roles?.length < ROLES_TO_DISPLAY && (
                    <>
                        {Array.from({ length: ROLES_TO_DISPLAY - roles?.length }).map((_, ix) => (
                            <RolesTableHeadCell key={ix} empty={true} />
                        ))}
                    </>
                )}
            </TableRow>
        </TableHead>
    )
}
